// -----------------------------------------------------------------------------
// AIDAS Platform — useData.js
// -----------------------------------------------------------------------------
//  Safe data fetch hook that automatically injects auth token
//  Waits for token from AuthContext before requesting
//  Prevents 401s and "token is not defined" errors
//  Supports Silent Refresh (background updates)
//  Module-level short-TTL cache: navigating back shows data instantly
// -----------------------------------------------------------------------------

import { useState, useEffect, useCallback, useRef } from "react";
import { apiFetch } from "../api";
import { useAuth } from "../context/useAuth";

// Module-level cache shared across all hook instances and navigation.
// Key = endpoint string, Value = { data, timestamp }
// Short TTL (30s) means stale data is shown immediately on remount
// while a background refresh runs silently.
const _cache = new Map();
const CACHE_TTL_MS = 30_000; // 30 seconds

function getCached(endpoint) {
  const entry = _cache.get(endpoint);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    _cache.delete(endpoint);
    return null;
  }
  return entry.data;
}

function setCached(endpoint, data) {
  _cache.set(endpoint, { data, timestamp: Date.now() });
}

/**
 * useData - Generic hook to fetch protected data from API endpoints.
 * Automatically includes authentication token and re-fetches when token changes.
 *
 * @param {string} endpoint - The API endpoint (e.g. "/users/me")
 * @returns {{ data: any, isLoading: boolean, error: Error | null, refresh: (silent?: boolean) => void, setData: Function }}
 */
export const useData = (endpoint) => {
  const { token, loading: authLoading } = useAuth();

  // Initialise data from cache so the component renders immediately on remount
  const [data, setData] = useState(() => (endpoint ? getCached(endpoint) : null));
  const [isLoading, setIsLoading] = useState(() => !getCached(endpoint));
  const [error, setError] = useState(null);
  const [lastFetchedEndpoint, setLastFetchedEndpoint] = useState(null);
  // Used to force a re-fetch on demand (refresh() increments this)
  const [refreshTick, setRefreshTick] = useState(0);

  const fetchData = useCallback(async (silent = false) => {
    if (!endpoint) {
      setIsLoading(false);
      return;
    }
    try {
      // Background update? only if we have data and it's THE SAME endpoint
      const isActuallySilent = silent && !!data && (endpoint === lastFetchedEndpoint);
      if (!isActuallySilent) setIsLoading(true);

      const result = await apiFetch(endpoint, token);

      // REFERENTIAL STABILITY: Only update if data actually changed
      if (JSON.stringify(data) !== JSON.stringify(result)) {
        setData(result);
        setCached(endpoint, result);
        setLastFetchedEndpoint(endpoint);
      }

      setError(null);
    } catch (err) {
      // 410 Gone = feature retired. Treat as empty list so consumers don't hang.
      if (err?.status === 410 || err?.message?.includes('410')) {
        setData([]);
        setError(null);
      } else {
        console.error(`[useData] Failed to fetch ${endpoint}:`, err);
        setError(err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [endpoint, token, data, lastFetchedEndpoint]);

  // Keep a ref to the latest fetchData so refresh() is never stale
  const fetchDataRef = useRef(fetchData);
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  useEffect(() => {
    if (authLoading || !token) return;

    // If we already have cached data for this endpoint, do a background refresh
    // (no spinner). Otherwise show spinner + foreground fetch.
    const hasCachedData = !!getCached(endpoint);
    const isBackgroundRefresh =
      hasCachedData && endpoint === lastFetchedEndpoint && refreshTick === 0;
    fetchData(isBackgroundRefresh);
  }, [authLoading, token, endpoint, refreshTick]); // refreshTick forces a re-fetch on refresh()

  return {
    data,
    isLoading: isLoading || authLoading || !token,
    error,
    // refresh() always calls the LATEST fetchData via ref — no stale closure
    refresh: (silent = false) => {
      if (silent) {
        fetchDataRef.current(true);
      } else {
        setRefreshTick(t => t + 1);
      }
    },
    setData,
  };
};

export default useData;